import React, { useState, useEffect, useRef } from 'react';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import ChatService from '../services/ChatService';
import FileUrlTester from '../utils/fileUrlTester';
import { theme } from '../styles/theme';
import './ChatMessageBubble.css';

/**
 * ChatMessageBubble - Displays a single chat message
 * Matches Flutter ChatMessageBubble component design
 */
const ChatMessageBubble = ({ message, isMe, currentUserId, showTimestamp = true }) => {
  const bubbleColor = _getBubbleColor(message, isMe, currentUserId);
  const textColor = theme.colors.messageText;
  
  // Format time
  const timeText = _formatTime(message.createdAt);

  // Get avatar colors based on role
  const getAvatarBgColor = (role) => {
    const colors = {
      'driver': { bg: '#fed7aa', border: '#fdba74', text: '#9a3412' },
      'officeadmin': { bg: '#e9d5ff', border: '#c084fc', text: '#6b21a8' },
      'admin': { bg: '#cffafe', border: '#67e8f9', text: '#164e63' },
    };
    return colors[role?.toLowerCase()] || { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' };
  };

  const getInitials = (name) => {
    if (!name || name === 'undefined' || typeof name !== 'string') return '?';
    const parts = name.trim().split(' ').filter(p => p && p !== 'undefined');
    if (parts.length >= 2) {
      return (parts[0][0]?.toUpperCase() || '') + (parts[1][0]?.toUpperCase() || '');
    }
    return name[0]?.toUpperCase() || '?';
  };

  const avatarColors = getAvatarBgColor(message.senderRole);
  const senderInitials = getInitials(message.senderName || 'User');
  const formattedTime = message.createdAt ? format(new Date(message.createdAt), 'h:mm a') : '';

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '0.125rem',
      maxWidth: isMe ? '65%' : '70%',
      width: 'fit-content',
      minWidth: '120px',
      marginLeft: isMe ? 'auto' : '0',
      alignItems: isMe ? 'flex-end' : 'flex-start',
      marginBottom: '0.125rem',
      padding: '0.125rem 0'
    }}>
      {!isMe && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem' }}>
          <div 
            style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '9999px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700,
              border: `1px solid ${avatarColors.border}80`,
              backgroundColor: avatarColors.bg,
              color: avatarColors.text
            }}
          >
            {senderInitials}
          </div>
              <div
                className="message-bubble-in group"
                style={{
                  backgroundColor: '#FFFFFF',
                  padding: (message.messageType === 'audio' || message.messageType === 'voice') ? '0.5rem 0.75rem' : '0.5rem 0.75rem 0.375rem 0.75rem',
                  borderRadius: '0.5rem',
                  borderTopLeftRadius: '0.25rem',
                  borderBottomLeftRadius: '0.5rem',
                  borderBottomRightRadius: '0.5rem',
                  borderTopRightRadius: '0.5rem',
                  boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
                  position: 'relative',
                  maxWidth: '500px',
                  minWidth: '120px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
            {/* Role label */}
            {message.senderRole && (
              <span 
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  marginBottom: '0.375rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  opacity: 0.9,
                  color: avatarColors.text
                }}
              >
                {message.senderRole.charAt(0).toUpperCase() + message.senderRole.slice(1)}
              </span>
            )}
            
            {/* File content */}
            {_renderFileContent(message, formattedTime, isMe)}
            
            {/* Message text - Hide for audio/voice messages (label is in player) */}
            {message.message && (message.messageType !== 'audio' && message.messageType !== 'voice') && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: '0.5rem',
                marginTop: message.fileUrl ? '0.5rem' : '0',
                flexWrap: 'wrap'
              }}>
                <p style={{ 
                  color: '#303030', 
                  fontSize: '0.875rem', 
                  lineHeight: '1.4', 
                  margin: 0,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flex: '1 1 auto',
                  minWidth: 0
                }}>
                {message.message}
              </p>
                {/* Timestamp inline at bottom right for received messages */}
                <span style={{
                  fontSize: '0.6875rem',
                  color: '#667781',
                  fontWeight: 500,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  marginLeft: '0.25rem',
                  alignSelf: 'flex-end',
                  paddingBottom: '0.125rem'
                }}>
                {formattedTime}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {isMe && (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '0.125rem',
          alignItems: 'flex-end',
          marginLeft: 'auto',
          maxWidth: '65%',
          width: 'fit-content',
          minWidth: '120px'
        }}>
          <div 
            className="message-bubble-out group"
            style={{
              backgroundColor: '#DCF8C6',
              padding: (message.messageType === 'audio' || message.messageType === 'voice') ? '0.5rem 0.75rem' : '0.5rem 0.75rem 0.375rem 0.75rem',
              borderRadius: '0.5rem',
              borderTopRightRadius: '0.25rem',
              borderBottomLeftRadius: '0.5rem',
              borderBottomRightRadius: '0.5rem',
              borderTopLeftRadius: '0.5rem',
              boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
              position: 'relative',
              maxWidth: '500px',
              minWidth: '120px',
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {/* File content */}
            {_renderFileContent(message, formattedTime, isMe)}
            
            {/* Message text - Hide for audio/voice messages (label is in player) */}
            {message.message && (message.messageType !== 'audio' && message.messageType !== 'voice') && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: '0.5rem',
                marginTop: message.fileUrl ? '0.5rem' : '0',
                flexWrap: 'wrap'
              }}>
                <p style={{ 
                  color: '#303030', 
                  fontSize: '0.875rem', 
                  lineHeight: '1.4', 
                  margin: 0,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flex: '1 1 auto',
                  minWidth: 0
                }}>
                {message.message}
              </p>
                {/* Timestamp and read status inline at bottom right for sent messages */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                  gap: '0.125rem',
                  flexShrink: 0,
                  marginLeft: '0.25rem',
                  alignSelf: 'flex-end',
                  paddingBottom: '0.125rem'
              }}>
                <span style={{
                    fontSize: '0.6875rem',
                    color: '#667781',
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                }}>
                  {formattedTime}
                </span>
                  {_buildReadStatus(message, isMe)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Get bubble color - WhatsApp style
 */
function _getBubbleColor(message, isMe, currentUserId) {
  const isMyOwnMessage = message.senderId === currentUserId;
  
  if (isMyOwnMessage) {
    return theme.colors.messageSent; // WhatsApp green
  }
  
  return theme.colors.messageReceived; // White
}

/**
 * Build role label for other users
 */
function _buildRoleLabel(senderRole) {
  let roleLabel = '';
  let labelColor = theme.colors.roleAdmin;
  
  switch (senderRole?.toLowerCase()) {
    case 'admin':
      roleLabel = 'Admin';
      labelColor = theme.colors.roleAdmin;
      break;
    case 'officeadmin':
      roleLabel = 'Office Admin';
      labelColor = theme.colors.roleOfficeAdmin;
      break;
    case 'driver':
      roleLabel = 'Driver';
      labelColor = theme.colors.roleDriver;
      break;
    default:
      roleLabel = senderRole || 'User';
  }
  
  return (
    <div className="role-label" style={{ color: labelColor }}>
      {roleLabel}
    </div>
  );
}

/**
 * Build read status (Material Icon)
 * Shows proper states: sent (single tick), delivered (double grey tick), read (double blue tick)
 */
function _buildReadStatus(message, isMe) {
  // Only show read status for sent messages (not received messages)
  if (!isMe) {
    return null; // Don't show read status for received messages
  }
  
  // Check if message has a real ID (not a temp optimistic message)
  // More lenient check: if message has any ID that's not a temp ID, consider it delivered
  const messageId = message.id || message.messageId || message._id;
  const hasRealId = messageId && 
                    !messageId.toString().startsWith('temp-') && 
                    messageId.toString().length > 0 &&
                    messageId !== 'undefined' &&
                    messageId !== 'null' &&
                    messageId !== null &&
                    messageId !== undefined;
  
  // Also check if message has a createdAt timestamp that's not "just now" (more than 1 second old)
  // This helps identify messages that have been sent to the server
  const isOldEnoughToBeDelivered = message.createdAt && 
                                    (Date.now() - new Date(message.createdAt).getTime()) > 1000;
  
  // Consider message delivered if it has a real ID OR if it's old enough (sent more than 1 second ago)
  const isDelivered = hasRealId || isOldEnoughToBeDelivered;
  
  // Read - Blue double tick (WhatsApp style - read)
  if (message.isRead === true || message.isRead === 1 || message.isRead === 'true') {
    return (
      <span className="material-icons-outlined" style={{ 
        fontSize: '13px', 
        color: '#53BDEB', // WhatsApp blue for read
        lineHeight: '1',
        marginLeft: '0.125rem'
      }}>
        done_all
      </span>
    );
  } 
  // Delivered - Grey double tick (message has real ID or is old enough to be delivered)
  else if (isDelivered) {
    return (
      <span className="material-icons-outlined" style={{ 
        fontSize: '13px', 
        color: '#667781', // WhatsApp grey for delivered
        lineHeight: '1',
        marginLeft: '0.125rem'
      }}>
        done_all
      </span>
    );
  } 
    // Sent - Single grey tick (optimistic message, not yet delivered)
  else {
    return (
      <span className="material-icons-outlined" style={{ 
        fontSize: '13px', 
        color: '#667781', // WhatsApp grey for sent
        lineHeight: '1',
        marginLeft: '0.125rem'
      }}>
        done
      </span>
    );
  }
}

/**
 * Format file size (bytes to human readable)
 */
function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Format time (WhatsApp style)
 */
function _formatTime(date) {
  if (!date) return '';
  
  try {
    const messageDate = new Date(date);
    
    if (isToday(messageDate)) {
      return format(messageDate, 'h:mm a');
    } else if (isYesterday(messageDate)) {
      return 'Yesterday';
    } else {
      const daysDiff = Math.floor((Date.now() - messageDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff < 7) {
        return format(messageDate, 'EEEE'); // Day name
      } else {
        return format(messageDate, 'MMM d'); // Month day
      }
    }
  } catch (e) {
    console.error('Error formatting time:', e);
    return '';
  }
}

/**
 * Render file content based on message type
 */
function _renderFileContent(message, formattedTime = null, isMe = false) {
  if (!message.messageType || message.messageType === 'text' || !message.fileUrl) {
    if ((message.messageType === 'voice' || message.messageType === 'audio') && !message.fileUrl) {
      console.error('❌ Voice/Audio message missing fileUrl!', {
        message: message,
        messageType: message.messageType,
        fileUrl: message.fileUrl
      });
    }
    return null;
  }

  // Handle different URL types:
  // 1. Blob URLs (for optimistic messages): blob:http://...
  // 2. Full URLs: http://...
  // 3. Relative URLs: /uploads/chat/...
  let fileUrl = message.fileUrl;
  
  if (!fileUrl) {
    console.error('❌ No fileUrl provided for message:', message);
    return null;
  }
  
  // CRITICAL: Get roomId from message to normalize fileUrl if needed
  const roomId = message.roomId;
  
  // Use ChatService to get full file URL (handles all cases)
  // Pass roomId to normalize fileUrl to api/chat/files/{roomId}/ format if needed
  fileUrl = ChatService.getFullFileUrl(fileUrl, roomId);

  switch (message.messageType) {
    case 'image':
      return <ImageMessageComponent message={message} fileUrl={fileUrl} formattedTime={formattedTime} isMe={isMe} />;

    case 'audio':
    case 'voice': // Backend returns 'voice' for audio messages
      return (
        <AudioMessageComponent message={message} fileUrl={fileUrl} formattedTime={formattedTime} isMe={isMe} />
      );

    case 'video':
      return (
        <VideoMessageComponent message={message} fileUrl={fileUrl} formattedTime={formattedTime} isMe={isMe} />
      );

    case 'file':
    case 'pdf':
      return (
        <FileMessageComponent message={message} fileUrl={fileUrl} formattedTime={formattedTime} isMe={isMe} />
      );

    default:
      return null;
  }
}

/**
 * Format file size
 */
function _formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Format duration (seconds to MM:SS or HH:MM:SS)
 */
function _formatDuration(seconds) {
  if (!seconds || !isFinite(seconds) || isNaN(seconds) || seconds < 0) {
    return '0:00';
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Image Message Component with error handling and retry
 */
const ImageMessageComponent = ({ message, fileUrl, formattedTime, isMe = false }) => {
  const [imageUrl, setImageUrl] = useState(fileUrl);
  const [hasError, setHasError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);

  // Load image - fileUrl is already processed by _renderFileContent
  // Just use it directly (it's already a full URL in correct format)
  useEffect(() => {
    if (!fileUrl) return;
    
    // Blob URLs and file:// URLs - use directly
    if (fileUrl.startsWith('blob:') || fileUrl.startsWith('file://')) {
      setImageUrl(fileUrl);
      if (fileUrl.startsWith('blob:')) {
        setBlobUrl(fileUrl);
      }
      return;
    }

    // fileUrl is already processed by _renderFileContent with roomId
    // It should already be a full URL in format: http://192.168.1.188:8001/api/chat/files/{roomId}/{filename}
    // Use the fileUrl directly (already processed)
    setImageUrl(fileUrl);

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [fileUrl]);

  const handleError = async (e) => {
    const failedUrl = e.target.src;
    
    // Try to resolve file URL using FileUrlResolver (for older files stored in different locations)
    // This handles cases where files might be in /uploads/chat/ instead of /api/chat/files/
    try {
      // Import FileUrlResolver dynamically
      const { getFileUrlResolver } = await import('../utils/fileUrlResolver');
      const resolver = getFileUrlResolver(ChatService.getFileBaseUrl());
      
      // Try to resolve the file URL (tries multiple patterns)
      const resolvedUrl = await resolver.resolveFileUrl(failedUrl, {
        logAttempts: false // Reduce console noise
      });
      
      if (resolvedUrl && resolvedUrl !== failedUrl) {
        // Try the resolved URL
        e.target.src = resolvedUrl;
        return; // Don't show error yet - let it try the new URL
      }
      
      // If FileUrlResolver didn't find alternative, try authenticated fetch as last resort
      const authenticatedUrl = await ChatService.getAuthenticatedFileUrl(failedUrl);
      
      if (authenticatedUrl && authenticatedUrl.startsWith('blob:')) {
        setImageUrl(authenticatedUrl);
        setBlobUrl(authenticatedUrl);
        return; // Don't show error - using authenticated URL
      }
    } catch (resolveError) {
      console.warn('⚠️ FileUrlResolver failed:', resolveError);
    }
    
    // All attempts failed - show error message
    console.error('❌ Failed to load image - file not found on server:', {
      fileUrl: failedUrl,
      originalFileUrl: message.fileUrl,
      fileName: message.fileName,
      messageId: message.id
    });
    
    setHasError(true);
    e.target.style.display = 'none';
    const errorDiv = e.target.nextSibling;
    if (errorDiv) {
      errorDiv.style.display = 'block';
    }
  };

  const handleLoad = () => {
    setHasError(false);
  };

  return (
    <div className="message-file-container" style={{
      backgroundColor: isMe ? '#DCF8C6' : '#FFFFFF',
      borderRadius: '0.5rem',
      padding: '0.25rem',
      display: 'inline-block',
      maxWidth: '300px',
      width: '100%',
      transition: 'all 0.2s',
      boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)'
    }}
    >
        <img 
        src={imageUrl} 
        alt={message.message || message.fileName || 'Image'} 
        className="message-image"
        style={{ 
          maxWidth: '100%',
          maxHeight: '300px',
          width: 'auto',
          height: 'auto',
          borderRadius: '0.375rem', 
          display: hasError ? 'none' : 'block',
          objectFit: 'cover',
          cursor: 'pointer',
          transition: 'opacity 0.2s'
        }}
        onError={handleError}
        onLoad={handleLoad}
        onClick={() => setShowFullImage(true)}
    onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.9';
    }}
    onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        loading="lazy"
      />
      
      {/* Full Screen Image Modal */}
      {showFullImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            cursor: 'pointer'
          }}
          onClick={() => setShowFullImage(false)}
        >
          <div 
            style={{
              position: 'relative',
              maxWidth: '100%',
              maxHeight: '100%',
        display: 'flex',
        alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowFullImage(false)}
              style={{
                position: 'absolute',
                top: '-3rem',
                right: 0,
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '2.5rem',
                height: '2.5rem',
                color: 'white',
                fontSize: '1.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
                zIndex: 10001
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              ✕
            </button>
            <img
              src={imageUrl}
              alt={message.message || message.fileName || 'Image'}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                width: 'auto',
                height: 'auto',
                borderRadius: '8px',
                objectFit: 'contain',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            {message.message && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '-3rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                  fontSize: '0.875rem',
                  textAlign: 'center',
                  maxWidth: '80%',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  borderRadius: '0.5rem'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {message.message}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Caption text if present */}
      {message.message && (
        <div style={{
          padding: '0.375rem 0.5rem 0.25rem 0.5rem',
          color: '#303030',
          fontSize: '0.875rem',
          lineHeight: '1.4',
          wordBreak: 'break-word'
        }}>
          {message.message}
        </div>
      )}
      
      {/* Timestamp and Read Status at Bottom - WhatsApp Style */}
        {formattedTime && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
          justifyContent: isMe ? 'flex-end' : 'flex-start',
          gap: '0.25rem',
          padding: '0.25rem 0.5rem 0.25rem 0.5rem'
          }}>
            <span style={{
            fontSize: '0.6875rem',
            color: '#667781',
            fontWeight: 500
            }}>
              {formattedTime}
            </span>
          {isMe && _buildReadStatus(message, isMe)}
          </div>
        )}
      
      <div className="message-file-error" style={{ 
        display: hasError ? 'block' : 'none',
        padding: '8px',
        backgroundColor: '#fee',
        color: '#c33',
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        <div style={{ fontWeight: '500' }}>Failed to load image</div>
        {message.fileName && (
          <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>
            {message.fileName}
          </div>
        )}
        <div style={{ fontSize: '10px', marginTop: '6px', opacity: 0.6, fontStyle: 'italic' }}>
          File not found on server - may have been moved or deleted
        </div>
      </div>
    </div>
  );
};

/**
 * Audio Message Component with custom player widget (matches Flutter design)
 */
const AudioMessageComponent = ({ message, fileUrl, formattedTime, isMe = false }) => {
  const [audioUrl, setAudioUrl] = useState(fileUrl);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(message.duration || 0);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);
  
  // Upload progress for sending state (75-80% as shown in image)
  const [uploadProgress] = useState(() => Math.random() * 5 + 75); // 75-80%

  useEffect(() => {
    if (!fileUrl) return;
    
    // fileUrl is already processed by _renderFileContent with roomId
    // It should already be a full URL in format: http://192.168.1.188:8001/api/chat/files/{roomId}/{filename}
    // Use the fileUrl directly (already processed by _renderFileContent)
    setAudioUrl(fileUrl);
  }, [fileUrl, message.roomId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    // CRITICAL: Reload audio when URL changes
    audio.load();

    const updateTime = () => {
      if (audio && !isNaN(audio.currentTime)) {
        setCurrentTime(audio.currentTime);
      }
    };
    
    const updateDuration = () => {
      if (audio && audio.duration && !isNaN(audio.duration)) {
        const audioDuration = Math.floor(audio.duration);
        setDuration(audioDuration);
      }
    };
    
    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };
    
    const handlePause = () => {
      setIsPlaying(false);
    };
    
    const handleLoadStart = () => {
      setIsLoading(true);
    };
    
    const handleCanPlay = () => {
      setIsLoading(false);
      updateDuration();
    };
    
    const handleError = async (e) => {
      const failedUrl = audio.currentSrc || audioUrl;
      const error = audio.error;
      
      console.error('❌ Audio error:', {
        code: error?.code,
        message: error?.message,
        url: failedUrl
      });
      
      // Try authenticated fetch if direct URL fails (for CORS/auth issues)
      if (error?.code === 2 || error?.code === 4) {
        try {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          if (token) {
            const response = await fetch(failedUrl, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              setAudioUrl(blobUrl);
              return; // Don't show error, try blob URL
            }
          }
        } catch (authError) {
          console.warn('⚠️ Authenticated fetch failed:', authError);
        }
      }
      
      // Try to resolve URL if it's a 404 or network error
      if (error?.code === 4 || error?.code === 2) {
        try {
          const { getFileUrlResolver } = await import('../utils/fileUrlResolver');
          const resolver = getFileUrlResolver(ChatService.getFileBaseUrl());
          const resolvedUrl = await resolver.resolveFileUrl(failedUrl, { logAttempts: false });
          
          if (resolvedUrl && resolvedUrl !== failedUrl) {
            setAudioUrl(resolvedUrl);
            return;
          }
        } catch (error) {
          console.warn('⚠️ Audio URL resolution failed:', error);
        }
      }
      
      setHasError(true);
      setIsLoading(false);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', handleCanPlay);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('canplaythrough', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl, message.duration]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      console.error('❌ Cannot play: audio element or URL missing');
      return;
    }

    try {
      if (isPlaying) {
        audio.pause();
      } else {
        // Ensure audio is loaded before playing
        if (audio.readyState === 0) {
          audio.load();
          // Wait for canplay event
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Audio load timeout'));
            }, 5000);
            
            const onCanPlay = () => {
              clearTimeout(timeout);
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (e) => {
              clearTimeout(timeout);
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              reject(e);
            };
            
            audio.addEventListener('canplay', onCanPlay, { once: true });
            audio.addEventListener('error', onError, { once: true });
          });
        }
        
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
      }
    } catch (err) {
      console.error('❌ Error playing audio:', err);
      setHasError(true);
      setIsLoading(false);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    audio.currentTime = newTime;
  };

  // Fix invalid duration values (Infinity, NaN, etc.)
  let validDuration = duration || message.duration || 0;
  if (!isFinite(validDuration) || isNaN(validDuration) || validDuration < 0) {
    validDuration = 0;
  }
  
  const displayDuration = validDuration;
  const progress = validDuration > 0 ? (currentTime / validDuration) * 100 : 0;

  // Check if message is in sending state (optimistic message or has status)
  const isSending = message.status === 'sending' || 
                    message.status === 'pending' || 
                    (message.id && message.id.toString().startsWith('temp-')) ||
                    (fileUrl && fileUrl.startsWith('blob:'));

  // Generate waveform bars for sending state
  const [waveformBars] = useState(() => {
    return Array.from({ length: 20 }, () => Math.random() * 20 + 8);
  });
  
  // Animate waveform when sending
  const [animatedWaveform, setAnimatedWaveform] = useState(waveformBars);
  
  useEffect(() => {
    if (isSending) {
      const interval = setInterval(() => {
        setAnimatedWaveform(Array.from({ length: 20 }, () => Math.random() * 20 + 8));
      }, 200);
      return () => clearInterval(interval);
    }
  }, [isSending]);

  // Format duration for display
  const formatDurationDisplay = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (hasError) {
    return (
      <div className="message-file-error" style={{ 
        padding: '12px', 
        backgroundColor: '#fee', 
        color: '#c33', 
        borderRadius: '8px',
        marginBottom: message.message ? '8px' : '0'
      }}>
        <div style={{ fontWeight: '500', marginBottom: '4px' }}>Failed to load audio</div>
        {message.fileName && (
          <div style={{ fontSize: '12px', opacity: 0.8 }}>{message.fileName}</div>
        )}
      </div>
    );
  }

  // WhatsApp-style sending UI - Matching image design
  if (isSending && isMe) {
    const sendingTime = formattedTime || (message.createdAt ? format(new Date(message.createdAt), 'h:mm a') : '');
    
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 14px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.08)',
        maxWidth: '350px',
        minWidth: '240px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        position: 'relative'
      }}>
        {/* Status Indicator - Circular with orange arc progress and blue X cancel */}
        <div 
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
            cursor: 'pointer'
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Handle cancel action - could emit event or call callback
          }}
        >
          {/* Orange progress arc (75-80% of circle) */}
          <svg 
            width="36" 
            height="36" 
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0,
              transform: 'rotate(-90deg)' // Start from top
            }}
          >
            <circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke="#ff6b35"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 15}`}
              strokeDashoffset={`${2 * Math.PI * 15 * (1 - uploadProgress / 100)}`}
              style={{
                transition: 'stroke-dashoffset 0.3s ease'
              }}
            />
          </svg>
          
          {/* Blue X icon in center */}
          <svg 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#4A90E2" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{ position: 'relative', zIndex: 1 }}
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>

        {/* Waveform and Duration */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
          {/* Waveform Bars */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            height: '24px',
            justifyContent: 'flex-start',
            paddingLeft: '2px'
          }}>
            {animatedWaveform.map((height, index) => (
              <div
                key={index}
                style={{
                  width: '3px',
                  height: `${height}px`,
                  backgroundColor: '#9ca3af',
                  borderRadius: '1.5px',
                  transition: 'height 0.2s ease'
                }}
              />
            ))}
          </div>
          
          {/* Duration - Below waveform */}
          <div style={{
            fontSize: '12px',
            color: '#9ca3af',
            fontWeight: '400',
            marginTop: '2px',
            lineHeight: '1.2'
          }}>
            {formatDurationDisplay(displayDuration || 0)}
          </div>
        </div>

        {/* Sending Status and Timestamp - Right side */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '2px',
          flexShrink: 0,
          paddingLeft: '8px'
        }}>
          <span style={{
            fontSize: '13px',
            color: '#ff6b35',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            lineHeight: '1.3'
          }}>
            Sending...
          </span>
          {sendingTime && (
            <span style={{
              fontSize: '11px',
              color: '#9ca3af',
              fontWeight: '400',
              whiteSpace: 'nowrap',
              lineHeight: '1.2'
            }}>
              {sendingTime}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="message-audio-container" style={{ 
      margin: 0,
      marginBottom: 0,
      padding: 0,
      maxWidth: '220px',
      width: '100%',
      minWidth: '200px',
      display: 'inline-block'
    }}>
      {/* Hidden audio element */}
      <audio 
        ref={audioRef}
        preload="metadata"
        crossOrigin="anonymous"
        style={{ display: 'none' }}
        key={audioUrl} // Force re-render when URL changes
      >
        <source src={audioUrl} type={message.fileType || 'audio/mpeg'} />
        <source src={audioUrl} type="audio/mp3" />
        <source src={audioUrl} type="audio/m4a" />
        <source src={audioUrl} type="audio/wav" />
        Your browser does not support the audio element.
      </audio>

      {/* Ultra Compact Audio Player Widget - No extra container */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        padding: '6px 10px 6px 10px',
        backgroundColor: '#fcfbf7',
        borderRadius: '10px',
        border: '1px solid rgba(0,0,0,0.08)',
        minHeight: '32px',
        transition: 'all 0.2s',
        margin: 0,
        width: '100%',
        maxWidth: '240px',
        boxSizing: 'border-box'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#faf9f5';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#fcfbf7';
      }}
      >
        {/* File Type Label and Timestamp - Same Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: 0,
          padding: 0,
          gap: '4px',
          marginBottom: '1px'
        }}>
          <div style={{
            fontSize: '10px',
            fontWeight: '500',
            color: '#57534e',
            textTransform: 'capitalize',
            margin: 0,
            padding: 0,
            lineHeight: '1.3'
          }}>
            {message.messageType === 'voice' ? 'voice file' : 'audio file'}
          </div>
          {formattedTime && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              margin: 0,
              padding: 0
            }}>
              <span style={{
                fontSize: '10px',
                color: isMe ? 'rgba(154, 52, 18, 0.65)' : '#78716c',
                fontWeight: 500,
                margin: 0,
                padding: 0,
                lineHeight: '1.3'
              }}>
                {formattedTime}
              </span>
              {isMe && (
                <span className="material-icons-outlined" style={{ 
                  fontSize: '12px', 
                  color: 'rgba(245, 158, 11, 0.85)',
                  margin: 0,
                  padding: 0,
                  lineHeight: '1'
                }}>
                  done_all
                </span>
              )}
            </div>
          )}
        </div>

        {/* Player Controls - Compact */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', margin: 0, padding: 0, marginTop: '2px' }}>
          {/* Tiny Play/Pause Button */}
          <button
            onClick={togglePlayPause}
            disabled={isLoading}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#0d9488',
              color: 'white',
              cursor: isLoading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              flexShrink: 0,
              transition: 'all 0.2s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
              padding: 0,
              margin: 0
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.backgroundColor = '#115e59';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = '#0d9488';
            }}
          >
            {isLoading ? (
              <span className="material-icons-outlined" style={{ fontSize: '10px' }}>hourglass_empty</span>
            ) : isPlaying ? (
              <span className="material-icons-outlined" style={{ fontSize: '10px' }}>pause</span>
            ) : (
              <span className="material-icons-outlined" style={{ fontSize: '10px', marginLeft: '1px' }}>play_arrow</span>
            )}
          </button>

          {/* Compact Progress and Time */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '4px', margin: 0, padding: 0 }}>
            {/* Time Display - Smaller */}
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              color: '#57534e',
              minWidth: '36px',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: '1.3',
              margin: 0,
              padding: 0
            }}>
              {_formatDuration(currentTime)}
            </span>

            {/* Compact Progress Bar - Thinner */}
            <div
              onClick={handleProgressClick}
              style={{
                flex: 1,
                height: '3px',
                backgroundColor: 'rgba(0,0,0,0.12)',
                borderRadius: '2px',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                margin: 0,
                padding: 0
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: '#0d9488',
                  borderRadius: '2px',
                  transition: 'width 0.1s linear'
                }}
              />
            </div>

            {/* Duration - Smaller */}
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              color: '#78716c',
              minWidth: '36px',
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: '1.3',
              margin: 0,
              padding: 0
            }}>
              {_formatDuration(displayDuration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Video Message Component with error handling and URL resolution
 */
const VideoMessageComponent = ({ message, fileUrl, formattedTime, isMe = false }) => {
  const [videoUrl, setVideoUrl] = useState(fileUrl);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!fileUrl) return;
    const fullUrl = ChatService.getFullFileUrl(fileUrl);
    setVideoUrl(fullUrl);
  }, [fileUrl]);

  const handleError = async (e) => {
    const failedUrl = e.target.currentSrc || videoUrl;
    
    try {
      const { getFileUrlResolver } = await import('../utils/fileUrlResolver');
      const resolver = getFileUrlResolver(ChatService.getFileBaseUrl());
      const resolvedUrl = await resolver.resolveFileUrl(failedUrl, { logAttempts: false });
      
      if (resolvedUrl && resolvedUrl !== failedUrl) {
        setVideoUrl(resolvedUrl);
        return;
      }
    } catch (error) {
      console.warn('⚠️ Video URL resolution failed:', error);
    }
    
    setHasError(true);
  };

  if (hasError) {
    return (
      <div className="message-file-error" style={{ padding: '8px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
        Failed to load video - file may have been moved or deleted
      </div>
    );
  }

  return (
    <div className="message-file-container" style={{
      backgroundColor: '#fcfbf7',
      borderRadius: '10px',
      padding: '6px 10px 8px 10px',
      border: '1px solid rgba(0,0,0,0.08)',
      display: 'inline-block',
      maxWidth: '240px',
      width: '100%',
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#faf9f5';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = '#fcfbf7';
    }}
    >
      {/* File Type Label and Timestamp - Same Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: 0,
        padding: 0,
        gap: '4px',
        marginBottom: '4px'
      }}>
        <div style={{
          fontSize: '10px',
          fontWeight: '500',
          color: '#57534e',
          textTransform: 'capitalize',
          margin: 0,
          padding: 0,
          lineHeight: '1.3'
        }}>
          video file
        </div>
        {formattedTime && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            margin: 0,
            padding: 0
          }}>
            <span style={{
              fontSize: '9px',
              color: isMe ? 'rgba(154, 52, 18, 0.6)' : '#78716c',
              fontWeight: 500,
              margin: 0,
              padding: 0,
              lineHeight: '1.2'
            }}>
              {formattedTime}
            </span>
            {isMe && _buildReadStatus(message, isMe)}
          </div>
        )}
      </div>
        <video 
        controls 
        className="message-video"
        style={{ 
          maxWidth: '100%',
          maxHeight: '280px',
          width: 'auto',
          height: 'auto',
          borderRadius: '8px', 
          marginTop: '4px',
          marginBottom: message.message ? '6px' : '0',
          objectFit: 'cover'
        }}
        onError={handleError}
        onLoadedMetadata={(e) => {
          // Fix duration display if it's invalid
          const video = e.target;
          if (video.duration && isFinite(video.duration) && !isNaN(video.duration)) {
            // Duration is valid
          } else {
            console.warn('⚠️ Invalid video duration detected');
          }
        }}
      >
        <source src={videoUrl} type={message.fileType || 'video/mp4'} />
        Your browser does not support the video element.
      </video>
      {message.duration && isFinite(message.duration) && !isNaN(message.duration) && (
        <div className="message-video-duration" style={{ 
          fontSize: '11px', 
          color: '#78716c',
          marginTop: '4px'
        }}>
          Duration: {_formatDuration(message.duration)}
        </div>
      )}
    </div>
  );
};

/**
 * File/PDF Message Component with compact design
 */
const FileMessageComponent = ({ message, fileUrl, formattedTime, isMe = false }) => {
  return (
    <div className="message-file-container" style={{
      backgroundColor: isMe ? '#DCF8C6' : '#FFFFFF',
      borderRadius: '0.5rem',
      padding: '0.5rem 0.75rem',
      display: 'inline-block',
      maxWidth: '280px',
      width: '100%',
      transition: 'all 0.2s',
      boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.opacity = '0.95';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.opacity = '1';
    }}
    >
      {/* File Info Row - WhatsApp Style */}
      <a 
        href={fileUrl} 
        download={message.fileName || 'file'}
        target="_blank"
        rel="noopener noreferrer"
        className="message-file-link"
        style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '12px',
          padding: '0',
          textDecoration: 'none',
          color: '#1c1917',
          cursor: 'pointer',
          transition: 'all 0.2s',
          marginBottom: '6px'
        }}
        onClick={async (e) => {
          // Force download for files that might open in browser
          e.preventDefault();
          try {
            const headers = {};
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(fileUrl, {
              method: 'GET',
              headers: headers
            });
            
            if (response.ok) {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = message.fileName || 'file';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            } else {
              window.open(fileUrl, '_blank');
            }
          } catch (error) {
            console.warn('Download failed, opening in new tab:', error);
            window.open(fileUrl, '_blank');
          }
        }}
      >
        <span className="material-icons-outlined" style={{ 
          fontSize: '24px', 
          color: isMe ? '#25D366' : '#128C7E',
          flexShrink: 0
        }}>
          {message.messageType === 'pdf' ? 'picture_as_pdf' : 'insert_drive_file'}
        </span>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ 
            fontWeight: '500', 
            fontSize: '14px',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#1c1917',
            lineHeight: '1.4',
            marginBottom: '2px'
          }}>
            {message.fileName || (message.messageType === 'pdf' ? 'PDF Document' : 'File')}
          </div>
          {message.fileSize && (
            <div style={{ 
              fontSize: '12px', 
              color: '#78716c', 
              lineHeight: '1.3'
            }}>
              {_formatFileSize(message.fileSize)}
            </div>
          )}
        </div>
      </a>
      
      {/* Timestamp and Read Status at Bottom - WhatsApp Style */}
      {formattedTime && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMe ? 'flex-end' : 'flex-start',
          gap: '0.25rem',
          marginTop: '0.25rem',
          paddingTop: '0.125rem'
        }}>
          <span style={{
            fontSize: '0.6875rem',
            color: '#667781',
            fontWeight: 500
          }}>
            {formattedTime}
          </span>
          {isMe && _buildReadStatus(message, isMe)}
        </div>
      )}
    </div>
  );
};

export default ChatMessageBubble;

